import { Dispatch, SetStateAction, useMemo, useState } from 'react'

import { OrderHistorySearchCondition } from '@/common/api/modules/orderHistory'
import {
  ListFollowTableHeaderProps,
  makeRectPropsForTableHeadItem,
  useListScrollFollow
} from '@/common/app/list'
import { OrderHistory } from '@/common/model/master/orderHistory'

import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import OrderHistoryBulkOperationButton from '@/components/container/modules/recommend/orderHistory/table/OrderHistoryBulkOperationButton'
import OrderHistoryListRow from '@/components/container/modules/recommend/orderHistory/table/OrderHistoryListRow'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

const COLUMNS = [
  '受注日時',
  '顧客特定番号',
  '受注番号',
  'アイテムID',
  '単価（税抜き）',
  '購入数量',
  '取得元'
]

interface TheadProps extends ListFollowTableHeaderProps {
  selected: boolean
  allSelected: boolean
  onCheck(checked: boolean): void
  float?: boolean
}

const Thead = ({
  selected,
  allSelected,
  onCheck,
  float = false,
  rowRefs,
  rowRects
}: TheadProps) => {
  const rectProps = (index: number) =>
    makeRectPropsForTableHeadItem(rowRefs, rowRects, index, float)

  return (
    <>
      <TableHeadItem checkbox sticky={0} float={float} {...rectProps(0)}>
        <CheckBox name={'all'} checked={selected} onChange={onCheck} incomplete={!allSelected} />
      </TableHeadItem>
      {COLUMNS.map((col, i) => (
        <TableHeadItem key={col} float={float} {...rectProps(i + 1)}>
          <Text size={12} color={TextColor.SECONDARY}>
            {col}
          </Text>
        </TableHeadItem>
      ))}
    </>
  )
}

interface Props {
  siteId: number
  orders: OrderHistory[]
  total: number
  condition: OrderHistorySearchCondition
  setCondition: Dispatch<SetStateAction<OrderHistorySearchCondition>>
  onUpdatePage: (page: number) => void
  reload: () => void
}

const ORDER_HISTORY_COLUMN_LENGTH = COLUMNS.length + 1

const OrderHistoryListTable = ({
  siteId,
  orders,
  total,
  condition,
  onUpdatePage,
  reload
}: Props) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const allSelected = useMemo(
    () => orders.length > 0 && orders.every((o) => selectedIds.includes(o.id)),
    [orders, selectedIds]
  )

  const handleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? orders.map((o) => o.id) : [])
  }

  const listFollowProps = useListScrollFollow(ORDER_HISTORY_COLUMN_LENGTH)
  const { header } = listFollowProps

  const theadProps: TheadProps = {
    selected: selectedIds.length > 0,
    allSelected,
    onCheck: handleSelectAll,
    ...header
  }

  return (
    <ScrollFollowTable
      listFollowProps={listFollowProps}
      tableHead={<Thead {...theadProps} />}
      tableFloatHead={<Thead {...theadProps} float />}
      body={
        <>
          {orders.map((order) => (
            <OrderHistoryListRow
              key={order.id}
              order={order}
              selected={selectedIds.includes(order.id)}
              onSelect={() => handleSelect(order.id)}
              isScrolled={header.isScrolled}
            />
          ))}
        </>
      }
      condition={condition}
      onUpdatePage={onUpdatePage}
      total={total}
      selectedIdsQuantity={selectedIds.length}
      selectedNode={
        <OrderHistoryBulkOperationButton
          siteId={siteId}
          selectedIds={selectedIds}
          reload={reload}
        />
      }
    />
  )
}

export default OrderHistoryListTable
